import { useEffect, useMemo, useRef, useState } from "react";
import { docAmounts } from "../../utils/docAmounts";
import { AmountCell, StatusPill, numericColumn, paymentStatusTone } from "../../components/tableCells";
import PaymentsCell from "../../components/PaymentsCell";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import CancelledReceivedDialog, { useCancelledReceived } from "../../components/CancelledReceivedDialog";
import ListTotalsFooter from "../../components/ListTotalsFooter";
import {
  Grid,
  Typography,
  Breadcrumbs,
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Chip,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  Alert,
  FormHelperText,
  Divider,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { MaterialReactTable } from "material-react-table";
import { LocalizationProvider, MobileDatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Link from "@mui/material/Link";
import { useNavigate, useLocation } from "react-router-dom";
import { usePersistedState } from "../../utils/usePersistedState";
import { LoadingButton } from "@mui/lab";
import AddIcon from "@mui/icons-material/Add";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import EditIcon from "@mui/icons-material/Edit";
import HistoryIcon from "@mui/icons-material/History";
import PublishedWithChangesIcon from "@mui/icons-material/PublishedWithChanges";
import CancelIcon from "@mui/icons-material/Cancel";
import AssignmentReturnedIcon from "@mui/icons-material/AssignmentReturned";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import PaymentsIcon from "@mui/icons-material/Payments";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { PDFDocument } from "pdf-lib";
import dayjs from "dayjs";
import Layout from "../../layout/Layout";
import { AlertMessage } from "../../components/AlertMessage";
import ConfirmDialog from "../../components/ConfirmDialog";
import ReservationImpactTable from "../../components/ReservationImpactTable";
import { computeReservationImpact } from "../../utils/reservationImpact";
import { formatAED, formatNumber } from "../../utils/format";
import ListFilterBar, { emptyListFilters, inferPeriod } from "../../components/ListFilterBar";
import { apiData } from "../../services/api_service";
import AdvanceDetailsDialog from "../../components/AdvanceDetailsDialog";
import { getDocPaymentStatus, DOC_PAYMENT_STATUS_FILTER_OPTIONS } from "../../utils/docPaymentStatus";
import {
  fetchProformaQuoteListAPI,
  proformaQuoteBaseAPI,
  fetchCustomerByIdAPI,
  uploadToVercelAPI,
} from "../../config";
import PaymentAttachmentUpload from "../../components/PaymentAttachmentUpload";
import ConvertPaymentSummary from "../../components/ConvertPaymentSummary";

// Parse the proforma's stored line items (LONGTEXT JSON) into an array.
// payment_type is stored as 1/2 and payment_method as the form option id
// ("1", "2", ...), so both need mapping to text for the list columns.
const PAYMENT_TYPE_LABELS = { 1: "Credit", 2: "Immediate" };
const PAYMENT_METHOD_LABELS = {
  1: "Cash",
  2: "Card",
  3: "Cheque",
  5: "Bank Transfer",
  6: "Online Payment",
  7: "Bill to Company",
};
// "Bill to Company" (id 7) is no longer offered on CDN/Proforma. Documents
// already saved with it must pick another payment method to be converted.
const isBillToCompany = (value) =>
  String(value ?? "") === "7" ||
  String(value ?? "").trim().toLowerCase() === "bill to company";
const formatPaymentMethod = (value) =>
  value === null || value === undefined || value === ""
    ? "-"
    : PAYMENT_METHOD_LABELS[Number(value)] || String(value);

const parseProformaItems = (items) => {
  if (Array.isArray(items)) return items;
  if (typeof items === "string") {
    try {
      return JSON.parse(items);
    } catch {
      return [];
    }
  }
  return [];
};

// The invoice PDF's "Seller Details" / "Seller Bank Information" blocks are
// pulled from tbl_manage_companies id 5 — a single fixed row, hardcoded
// server-side ("LMGGT fixed company id", see generateOrderReceiptPdf in
// orderMasterModel.js). There's no API to fetch it separately, so it's
// mirrored here verbatim for the preview rather than adding a new endpoint
// just to re-fetch a constant.
const INVOICE_SELLER = {
  companyName: "LMG G GENERAL TRADING LLC",
  addressLine: "FLEXPRO UAE BADMINTON STORE",
  cityEmirate: "Al Qusais 1, Dubai",
  poBox: "19224",
  trn: "104430108100003",
  email: "marketing@lmggt.com",
  phone: "0545699500",
  bankName: "WIO BANK PJSC",
  accountName: "LMG G GENERAL TRADING LLC",
  iban: "AE200860000009338333464",
};

const ProformaQuoteList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Filters handed over by a dashboard card (date range, store, customer,
  // creator, status scope) so this list opens on exactly the rows the card
  // totalled. null = plain, unfiltered list; "Clear filter" resets to that.
  const [dashFilter, setDashFilter] = usePersistedState("proforma-list:dashFilter", () => {
    const s = location.state || {};
    const positive = (v) => (Number(v) > 0 ? Number(v) : undefined);
    const f = {
      from_date: s.fromDate || undefined,
      to_date: s.toDate || undefined,
      store_id: positive(s.storeId),
      customer_id: positive(s.customerId),
      created_by: positive(s.createdBy) ?? positive(s.salesPersonId),
      status_scope: s.statusScope || undefined,
      advance_filter: s.advanceFilter || undefined,
      payment_method: s.paymentMethod || undefined,
    };
    return Object.values(f).some((v) => v !== undefined) ? f : null;
  });
  // Filter bar above the table (Period / From–To / Store / Customer /
  // Salesperson / Search). Starts from whatever the dashboard card passed in
  // and writes into the same dashFilter the list already fetches with.
  const [bar, setBar] = usePersistedState("proforma-list:bar", () => {
    const st = location.state || {};
    const pos = (v) => (Number(v) > 0 ? Number(v) : 0);
    return {
      ...emptyListFilters,
      period: inferPeriod(st.fromDate || "", st.toDate || ""),
      fromDate: st.fromDate || "",
      toDate: st.toDate || "",
      storeId: pos(st.storeId),
      customerId: pos(st.customerId),
      salesPersonId: pos(st.createdBy) || pos(st.salesPersonId),
    };
  });
  const applyBar = (next) => {
    setBar(next);
    setPageIndex(0);
    setDashFilter((prev) => {
      const merged = {
        ...(prev || {}),
        from_date: next.fromDate || undefined,
        to_date: next.toDate || undefined,
        store_id: Number(next.storeId) > 0 ? Number(next.storeId) : undefined,
        customer_id: Number(next.customerId) > 0 ? Number(next.customerId) : undefined,
        created_by: Number(next.salesPersonId) > 0 ? Number(next.salesPersonId) : undefined,
        q: next.search || undefined,
        product_ids: (next.productIds || []).length ? next.productIds.join(",") : undefined,
      };
      return Object.values(merged).some((v) => v !== undefined) ? merged : null;
    });
  };
  const alertRef = useRef(null);
  const [proformaList, setProformaList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  // Sums over every row the current filters match (all pages), from the API.
  const [listTotals, setListTotals] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pageIndex, setPageIndex] = usePersistedState("proforma-list:pageIndex", 0);
  const [pageSize, setPageSize] = usePersistedState("proforma-list:pageSize", 100);
  // { id, type: "convert" | "cancel" } while a confirmation is pending
  const [confirm, setConfirm] = useState(null);
  // "Payments Received" dialog opened from the Received amount.
  const [advanceDialog, setAdvanceDialog] = useState({ open: false, url: "", number: "" });
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [cancelledOpen, setCancelledOpen] = useState(false);
  // Columns shown by default: the ones used every day. The rest (phone / email are
  // already under the company name) can be switched on from the columns icon in
  // the table toolbar; the choice is remembered on this computer.
  const COLUMN_KEY = "lmggt:proforma-list:columns:v4";
  const DEFAULT_HIDDEN_COLUMNS = {
    customer_phone: false,
    customer_email: false,
    total_qty: false,
    payment_type: false,
    payment_method: false,
    last_payment_date: false,
    payment_reference_no: false,
    payment_attachment_url: false,
  };
  const [columnVisibility, setColumnVisibility] = useState(() => {
    try {
      const raw = localStorage.getItem(COLUMN_KEY);
      return raw ? JSON.parse(raw) : DEFAULT_HIDDEN_COLUMNS;
    } catch (e) {
      return DEFAULT_HIDDEN_COLUMNS;
    }
  });
  const changeColumnVisibility = (updater) => {
    const next = typeof updater === "function" ? updater(columnVisibility) : updater;
    setColumnVisibility(next);
    try {
      localStorage.setItem(COLUMN_KEY, JSON.stringify(next));
    } catch (e) {
      /* remembering the choice is a convenience only */
    }
  };
  const cancelledRecv = useCancelledReceived("proforma", null, listTotals);
  // { anchorEl, row } for the open row-actions kebab menu, else null
  const [actionMenu, setActionMenu] = useState(null);
  // { row, selectedItems: [], loading: false } for partial conversion dialog
  const [convertDialog, setConvertDialog] = useState(null);
  // Request Credit Approval dialog opened from the convert dialog
  // Mirrors the shared DeliveryNoteForm's paymentOptions (Item: mode of
  // payment shown/editable on Convert to Invoice).
  const paymentModeOptions = useMemo(
    () => [
      { value: "1", label: "Cash" },
      { value: "2", label: "Card" },
      { value: "3", label: "Cheque" },
      { value: "5", label: "Bank Transfer" },
      { value: "6", label: "Online Payment" },
      // { value: "7", label: "Bill to Company" },
    ],
    []
  );
  // { loading, data } for the converted/cancelled metadata dialog, else null
  const [metaDialog, setMetaDialog] = useState(null);
  // { loading, data: [] } for the "View GRN" dialog, else null
  const [grnDialog, setGrnDialog] = useState(null);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [mergeMode, setMergeMode] = useState("month");
  const [mergeFromMonth, setMergeFromMonth] = useState(dayjs().startOf("month"));
  const [mergeToMonth, setMergeToMonth] = useState(dayjs().startOf("month"));
  const [mergeFromDate, setMergeFromDate] = useState(dayjs().startOf("month"));
  const [mergeToDate, setMergeToDate] = useState(dayjs());
  const [mergeProformas, setMergeProformas] = useState([]);
  const [mergeSelectedIds, setMergeSelectedIds] = useState([]);
  const [mergeSearch, setMergeSearch] = useState("");
  const [mergeFetching, setMergeFetching] = useState(false);
  const [merging, setMerging] = useState(false);
  const [mergeMessage, setMergeMessage] = useState("");

  // Open the details dialog for a converted/cancelled proforma (who/when + the
  // invoice it became).
  const openMeta = async (row) => {
    setMetaDialog({ loading: true, data: null });
    try {
      const result = await apiData("get", `${proformaQuoteBaseAPI}/${row.id}`);
      setMetaDialog({ loading: false, data: result.success ? result.data : null });
    } catch (error) {
      console.error("Failed to fetch proforma details:", error);
      setMetaDialog({ loading: false, data: null });
    }
  };

  // Fetch and open the "View GRN" dialog for a Proforma that has one or more GRNs
  // already generated against it.
  const openGrnView = async (row) => {
    setGrnDialog({ loading: true, data: [] });
    try {
      const result = await apiData("get", `${proformaQuoteBaseAPI}/${row.id}/grns`);
      setGrnDialog({ loading: false, data: result.success ? result.data : [] });
    } catch (error) {
      console.error("Failed to fetch GRNs:", error);
      setGrnDialog({ loading: false, data: [] });
    }
  };

  // Fetch and open the partial-convert dialog. Uses the same grn-candidate
  // endpoint the "Generate GRN" screen uses, because a Proforma that's had a
  // return (partial_returned) still stores its ORIGINAL issued quantities in
  // row.items — returnable_quantity there is already "issued - already
  // returned", i.e. exactly what's still left to convert.
  const openConvertDialog = async (row) => {
    setConvertDialog({
      row,
      items: [],
      selectedItems: [],
      convertQuantities: {},
      // Pre-fill with whatever payment details are already recorded on the
      // Proforma — the user can change any of them here, or leave as-is and
      // it's carried through to the invoice unchanged.
      paymentType: row.payment_type != null ? String(row.payment_type) : "",
      // A Bill to Company document starts blank so the user must choose a
      // new method before the Convert button enables.
      billToCompanySource: isBillToCompany(row.payment_method),
      paymentMethod:
        row.payment_method != null && !isBillToCompany(row.payment_method)
          ? String(row.payment_method)
          : "",
      paymentReferenceNo: row.payment_reference_no || "",
      paymentAttachmentUrl: row.payment_attachment_url || "",
      paymentAttachmentName: row.payment_attachment_name || "",
      paymentAttachmentUploading: false,
      loading: false,
      itemsLoading: true,
    });
    try {
      // Credit Days are Admin-configured per customer (see Customers.jsx) —
      // fetched alongside the convertible items so "Credit Payment Terms"
      // can be gated the same way the create form gates it.
      const [result, advRes] = await Promise.all([
        apiData("get", `${proformaQuoteBaseAPI}/${row.id}/grn-candidate`),
        // Advance payments still held on this Proforma, listed in the
        // Payment Summary so the user sees what's already been collected.
        apiData("get", `${proformaQuoteBaseAPI}/${row.id}/advances`).catch(() => null),
      ]);
      const heldAdvances = advRes?.success
        ? (advRes.data?.advances || []).filter((a) => Number(a.unapplied_amount) > 0)
        : [];
      const candidateItems = result.success ? result.data?.items || [] : [];
      const convertible = candidateItems
        .filter((i) => Number(i.returnable_quantity) > 0)
        .map((i) => ({
          product_id: i.product_id,
          product_name: i.product_name,
          sku: i.sku,
          quantity: Number(i.returnable_quantity),
          selling_price: Number(i.selling_price) || 0,
          total_price: Number(i.selling_price || 0) * Number(i.returnable_quantity),
          // Line discount is for the whole ISSUED qty; the preview pro-rates it.
          discount_amount: Number(i.discount_amount) || 0,
          issued_quantity: Number(i.issued_quantity) || 0,
        }));
      if (!result.success) {
        showAlert(false, result.message || "Failed to load convertible items.");
      }
      // Credit is approved per document: converting on Credit is only
      // allowed if THIS Proforma was itself created with Admin-approved credit,
      // and it keeps that document's days.
      const customerCreditDays =
        String(row.payment_type) === "1" && Number(row.credit_days) > 0
          ? Number(row.credit_days)
          : null;
      setConvertDialog((prev) => ({
        row,
        // grn-candidate already joins tbl_store/tbl_store(salesman), so the
        // store name, salesman name, emirate, address, TRN etc. all come
        // along for free for the Invoice Preview below — no separate lookup.
        storeName: result.data?.proforma?.store_name || null,
        sourceDoc: result.data?.proforma || null,
        customerCreditDays,
        items: convertible,
        selectedItems: convertible.map((i) => i.product_id), // default select all
        // Item 7: quantity to convert per line, editable, defaults to the
        // full available quantity — applies even with a single line.
        convertQuantities: Object.fromEntries(
          convertible.map((i) => [i.product_id, i.quantity])
        ),
        // Carry over whatever the user may have already changed while the
        // items were loading. If Credit was pre-filled but this customer
        // turns out not to have Credit Days configured, fall back to
        // Immediate Payment rather than submitting an invalid selection.
        paymentType:
          prev?.paymentType === "1" && customerCreditDays === null
            ? ""
            : prev?.paymentType ?? "",
        paymentMethod: prev?.paymentMethod ?? "",
        billToCompanySource: prev?.billToCompanySource ?? false,
        heldAdvances,
        paymentReferenceNo: prev?.paymentReferenceNo ?? "",
        paymentAttachmentUrl: prev?.paymentAttachmentUrl ?? "",
        paymentAttachmentName: prev?.paymentAttachmentName ?? "",
        paymentAttachmentUploading: false,
        loading: false,
        itemsLoading: false,
      }));
    } catch (error) {
      console.error("Failed to fetch convertible items:", error);
      showAlert(false, "Failed to load convertible items.");
      setConvertDialog(null);
    }
  };

  // Estimated invoice total for the selected items/quantities (same math as
  // the Invoice Preview); the server recomputes the exact figure on convert.
  const estimateConvertGrandTotal = (dlg) => {
    if (!dlg?.items?.length) return 0;
    const row = dlg.sourceDoc || dlg.row || {};
    const selected = dlg.items.filter((it) => dlg.selectedItems.includes(it.product_id));
    const qtyOf = (it) => Number(dlg.convertQuantities?.[it.product_id] ?? it.quantity) || 0;
    const netOf = (it, qty) => {
      const issued = Number(it.issued_quantity) || 0;
      const lineDisc = issued > 0 ? ((Number(it.discount_amount) || 0) * qty) / issued : 0;
      return Math.max(qty * (Number(it.selling_price) || 0) - lineDisc, 0);
    };
    const allNet = dlg.items.reduce((s, it) => s + netOf(it, Number(it.quantity) || 0), 0);
    const selectedNet = selected.reduce((s, it) => s + netOf(it, qtyOf(it)), 0);
    const ratio = allNet > 0 ? selectedNet / allNet : selected.length > 0 ? 1 : 0;
    return (
      ((Number(row.subtotal) || 0) +
        (Number(row.tax_amount) || 0) +
        (Number(row.shipping_amount) || 0)) *
      ratio
    );
  };

  // Payment Summary figures for the open convert dialog (null while loading).
  const convertSplit = (() => {
    if (!convertDialog || convertDialog.itemsLoading) return null;
    const invoiceTotal = Math.round(estimateConvertGrandTotal(convertDialog) * 100) / 100;
    const held = (convertDialog.heldAdvances || []).reduce(
      (s, a) => s + Number(a.unapplied_amount || 0),
      0
    );
    const advanceApplied = Math.min(held, invoiceTotal);
    const amountDue = Math.round((invoiceTotal - advanceApplied) * 100) / 100;
    return {
      invoiceTotal,
      advanceApplied,
      amountDue,
      fullyCovered: advanceApplied > 0 && amountDue <= 0.009,
    };
  })();

  const showAlert = (success, message) =>
    alertRef.current?.triggerAlert(
      JSON.stringify({ message, messageType: success ? "success" : "error" })
    );

  const fetchProformas = async () => {
    setLoading(true);
    try {
      const result = await apiData("get", fetchProformaQuoteListAPI, {
        pageIndex,
        pageSize,
        ...(dashFilter || {}),
      });
      if (result.success) {
        setProformaList(result.data?.result || []);
        setTotalCount(result.data?.totalCount || 0);
        setListTotals(result.data?.totals || null);
      }
    } catch (error) {
      console.error("Failed to fetch proforma invoices:", error);
    } finally {
      setLoading(false);
    }
  };

  // Config for the reusable confirmation dialog, driven by the pending action.
  const confirmConfig = {
    convert: {
      title: "Convert to Invoice",
      message:
        "Convert this Proforma Invoice to an invoice? A real order will be created and stock will be deducted.",
      confirmText: "Convert",
      confirmColor: "success",
      endpoint: (id) => `${proformaQuoteBaseAPI}/${id}/convert`,
      okMsg: "Converted to invoice.",
      failMsg: "Failed to convert.",
    },
    cancel: {
      title: "Cancel Proforma Invoice",
      message:
        "Cancel this Proforma Invoice? The reserved stock will be released.",
      confirmText: "Yes, Cancel",
      confirmColor: "error",
      endpoint: (id) => `${proformaQuoteBaseAPI}/${id}/cancel`,
      okMsg: "Proforma Invoice cancelled.",
      failMsg: "Failed to cancel.",
    },
  };

  // Open the Cancel dialog with a live preview of which reserved stock will be
  // released and how availability changes (before → after).
  const openCancel = async (row) => {
    setConfirm({
      id: row.id,
      type: "cancel",
      number: row.proforma_number,
      impact: null,
      impactLoading: true,
    });
    const rows = await computeReservationImpact({
      storeId: row.store_id,
      oldItems: parseProformaItems(row.items),
      newItems: [], // cancelling releases everything
    });
    setConfirm((prev) =>
      prev && prev.id === row.id && prev.type === "cancel"
        ? { ...prev, impact: rows, impactLoading: false }
        : prev
    );
  };

  const runConfirmedAction = async () => {
    if (!confirm) return;
    const cfg = confirmConfig[confirm.type];
    setConfirmLoading(true);
    try {
      const result = await apiData("post", cfg.endpoint(confirm.id), {});
      showAlert(result.success, result.msg || (result.success ? cfg.okMsg : cfg.failMsg));
      if (result.success) fetchProformas();
    } catch (error) {
      console.error("Action failed:", error);
      showAlert(false, error?.response?.data?.message || cfg.failMsg);
    } finally {
      setConfirmLoading(false);
      setConfirm(null);
    }
  };

  const handleExportData = async () => {
    try {
      setLoading(true);

      const payload = { pageIndex: 0, pageSize: 10000000, ...(dashFilter || {}) };
      const response = await apiData("get", fetchProformaQuoteListAPI, payload);
      const dataArray = Array.isArray(response.data?.result)
        ? response.data.result
        : [];
      if (dataArray.length === 0) {
        showAlert(false, "No data to export!");
        return;
      }

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Proforma Invoices", { views: [{ state: "frozen", ySplit: 1 }] });
      const num = (v) => Math.round((parseFloat(v) || 0) * 100) / 100;

      worksheet.columns = [
        { header: "#", key: "index", width: 8 },
        { header: "Proforma No", key: "proforma_number", width: 18 },
        { header: "Date", key: "order_date", width: 15 },
        { header: "Bill To Company", key: "customer_name", width: 30 },
        { header: "Product Name", key: "product_name", width: 36 },
        { header: "SKU", key: "sku", width: 18 },
        { header: "Qty", key: "qty", width: 10 },
        { header: "Unit Price (Excl. Tax)", key: "unit_ex", width: 16 },
        { header: "Unit Price (Incl. Tax)", key: "unit_inc", width: 16 },
        { header: "Discount %", key: "disc_pct", width: 11 },
        { header: "Discount Amount", key: "disc_amt", width: 15 },
        { header: "Taxable Value", key: "taxable", width: 15 },
        { header: "Product VAT", key: "line_vat", width: 13 },
        { header: "Line Total (Incl. Tax)", key: "line_total", width: 17 },
        { header: "Phone", key: "customer_phone", width: 20 },
        { header: "Email", key: "customer_email", width: 30 },
        { header: "Store", key: "store_name", width: 24 },
        { header: "Subtotal", key: "subtotal", width: 20 },
        { header: "Discount", key: "discount", width: 20 },
        { header: "VAT", key: "vat", width: 20 },
        { header: "Shipping", key: "shipping", width: 20 },
        { header: "Grand Total", key: "grand_total", width: 20 },
        { header: "Advance Received", key: "advance", width: 20 },
        { header: "Balance Due", key: "balance_due", width: 20 },
        { header: "Payment Status", key: "pay_status", width: 18 },
        { header: "Payment Type", key: "terms", width: 18 },
        { header: "Payment Method", key: "payment_method", width: 18 },
        { header: "Credit Days", key: "credit_days", width: 11 },
        { header: "Due Date", key: "due_date", width: 14 },
        { header: "Payment Date", key: "last_payment_date", width: 14 },
        { header: "Transaction Id", key: "transaction_id", width: 20 },
        { header: "Payment Attachment", key: "attachment", width: 24 },
        { header: "Created By", key: "created_by_name", width: 18 },
        { header: "Created At", key: "created_at", width: 14 },
        { header: "Last Updated", key: "updated_at", width: 14 },
        { header: "Status", key: "status", width: 15 },
      ];

      const fmtDate = (v) => (v ? dayjs(v).format("DD-MM-YYYY") : "");
      const AED = '"AED" #,##0.00';

      dataArray.forEach((item, index) => {
        const lines = parseProformaItems(item.items);

        // Same split as the CDN list: the document's discount and VAT are shared
        // across its lines in proportion to each line's value, so lines add up.
        const inclusive = item.tax_mode === "inclusive";
        const docDiscount = num(item.discount_amount);
        const docVat = num(item.tax_amount);
        const taxRate =
          parseFloat(item.tax_type) ||
          (() => {
            const base = Math.max(0, num(item.grand_total) - docVat - num(item.shipping_amount));
            return base > 0 && docVat > 0 ? Math.round((docVat / base) * 10000) / 100 : 0;
          })();
        const pdNets = lines.map((l) => {
          const gross = num(l.selling_price) * (parseFloat(l.quantity) || 0);
          const net =
            l.total_price !== undefined && l.total_price !== null && l.total_price !== ""
              ? num(l.total_price)
              : Math.max(0, gross - num(l.discount_amount));
          return { gross, net };
        });
        const netSum = pdNets.reduce((t, n) => t + n.net, 0);
        const lineVals = [];
        lines.forEach((l, i) => {
          const { gross, net } = pdNets[i];
          const alloc = netSum > 0 ? (docDiscount * net) / netSum : 0;
          const afterDisc = Math.max(0, net - alloc);
          const afterDiscSum = Math.max(0, netSum - docDiscount);
          const vat = afterDiscSum > 0 ? (docVat * afterDisc) / afterDiscSum : 0;
          const taxable = inclusive ? afterDisc - vat : afterDisc;
          const discAmt = Math.max(0, gross - net) + alloc;
          const unitEx = inclusive ? num(l.selling_price) / (1 + taxRate / 100) : num(l.selling_price);
          lineVals.push({
            unit_ex: num(unitEx),
            unit_inc: num(unitEx * (1 + taxRate / 100)),
            disc_pct: gross > 0 ? Math.round((discAmt / gross) * 10000) / 100 : 0,
            disc_amt: num(discAmt),
            taxable: num(taxable),
            line_vat: num(vat),
            line_total: num(inclusive ? afterDisc : taxable + vat),
          });
        });

        const nets = lines.map((l) => {
          const gross = num(l.selling_price) * (parseFloat(l.quantity) || 0);
          return l.total_price !== undefined && l.total_price !== null && l.total_price !== ""
            ? num(l.total_price)
            : Math.max(0, gross - num(l.discount_amount));
        });
        const isCredit = String(item.payment_type) === "1";
        const docFields = {
          index: index + 1,
          proforma_number: item.proforma_number || "NA",
          order_date: fmtDate(item.order_date) || "NA",
          customer_name: item.customer_name || "NA",
          customer_phone: item.customer_phone || "",
          customer_email: item.customer_email || "",
          store_name: item.store_name || "",
          subtotal: docAmounts(item).subtotal,
          discount: docAmounts(item).discount,
          vat: docAmounts(item).vat,
          shipping: docAmounts(item).shipping,
          grand_total: parseFloat(item.grand_total || 0),
          advance: parseFloat(item.advance_unapplied || 0),
          balance_due: parseFloat(item.balance_due || 0),
          pay_status: getDocPaymentStatus(item).label,
          terms: PAYMENT_TYPE_LABELS[Number(item.payment_type)] || "",
          payment_method: isCredit ? "Credit" : formatPaymentMethod(item.payment_method),
          credit_days: isCredit ? parseInt(item.credit_days, 10) || 0 : "",
          due_date: fmtDate(item.due_date),
          last_payment_date: fmtDate(item.last_payment_date),
          transaction_id: item.payment_reference_no || "",
          attachment: item.payment_attachment_url
            ? { text: "View file", hyperlink: item.payment_attachment_url }
            : "",
          created_by_name: item.created_by_name || "",
          created_at: fmtDate(item.created_at),
          updated_at: fmtDate(item.updated_at),
          status: item.status || "active",
        };

        // One block per Proforma: document fields written once on the first
        // product row and merged down, so each Proforma reads as a single row
        // with only the product columns repeating beneath it.
        const rowLines = lines.length ? lines : [null];
        const startRow = worksheet.rowCount + 1;
        rowLines.forEach((l, i) => {
          worksheet.addRow({
            ...(i === 0 ? docFields : {}),
            product_name: l ? l.product_name || "" : "",
            sku: l ? l.sku || "" : "",
            qty: l ? parseFloat(l.quantity) || 0 : "",
            ...(l ? lineVals[i] : {}),
          });
        });
        const endRow = worksheet.rowCount;
        if (endRow > startRow) {
          // Product columns 5-14 vary per row; all others merge down the block.
          const productCols = new Set([5, 6, 7, 8, 9, 10, 11, 12, 13, 14]);
          for (let c = 1; c <= worksheet.columns.length; c += 1) {
            if (!productCols.has(c)) worksheet.mergeCells(startRow, c, endRow, c);
          }
        }
      });

      // Total footer — use the SAME backend `listTotals` the on-screen footer
      // shows (totals over ALL filtered rows, never the current page), so the
      // Excel footer always matches the list footer. Cancelled-received line
      // uses the same `cancelledRecv` the screen renders.
      const isCancelledDoc = (r) => String(r.status || "").toLowerCase() === "cancelled";
      const counted = dataArray.filter((r) => !isCancelledDoc(r));
      const activeCount = listTotals?.active_count ?? counted.length;
      const cancelledExcluded = Math.max(0, dataArray.length - counted.length);
      const footer = worksheet.addRow({
        customer_name: `Total (${activeCount} ${activeCount === 1 ? "record" : "records"}${cancelledExcluded ? `, excl. ${cancelledExcluded} cancelled` : ""})`,
        grand_total: num(listTotals?.grand_total || 0),
        advance: num(listTotals?.advance_unapplied || 0),
        balance_due: num(listTotals?.balance_due || 0),
      });
      footer.font = { bold: true };
      if (cancelledRecv.total > 0) {
        const cancelledFooter = worksheet.addRow({
          customer_name: `Cancelled (${cancelledRecv.count}) — payment received`,
          advance: num(cancelledRecv.total),
        });
        cancelledFooter.getCell("advance").numFmt = "#,##0.00";
        cancelledFooter.getCell("advance").alignment = { horizontal: "left", vertical: "top" };
        cancelledFooter.font = { bold: true, color: { argb: "FFB42318" } };
      }
      ["unit_ex", "unit_inc", "disc_amt", "taxable", "line_vat", "line_total", "subtotal", "discount", "vat", "shipping", "grand_total", "advance", "balance_due"].forEach((k) => {
        worksheet.getColumn(k).numFmt = "#,##0.00";
      });
      worksheet.getColumn("disc_pct").numFmt = '0.00"%"';

      worksheet.getRow(1).eachCell((cell) => {
        cell.font = { bold: true, color: { argb: "FF000000" } };
        cell.alignment = { vertical: "middle", horizontal: "center" };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });

      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber > 1) {
          row.eachCell((cell) => {
            cell.alignment = {
              wrapText: true,
              vertical: "middle",
              horizontal: "left",
            };
            cell.border = {
              top: { style: "thin" },
              left: { style: "thin" },
              bottom: { style: "thin" },
              right: { style: "thin" },
            };
          });
        }
      });


      const buffer = await workbook.xlsx.writeBuffer();
      const fileName = `Proforma_List_${dayjs().format("DD-MM-YYYY")}.xlsx`;
      saveAs(new Blob([buffer]), fileName);

      showAlert(true, "Excel Downloaded Successfully!");
    } catch (error) {
      console.error("Export failed:", error);
      showAlert(false, "Failed to export data.");
    } finally {
      setLoading(false);
    }
  };

  const hasReceipt = (proforma) =>
    proforma?.pdf_url &&
    proforma.pdf_url !== "NA" &&
    String(proforma.pdf_url).trim() !== "";

  const openMergeDialog = async () => {
    setMergeMessage("");
    setMergeSelectedIds([]);
    setMergeSearch("");
    setMergeDialogOpen(true);
    await fetchProformasForMerge();
  };

  const fetchProformasForMerge = async () => {
    try {
      setMergeFetching(true);
      const response = await apiData("get", fetchProformaQuoteListAPI, {
        pageIndex: 0,
        pageSize: 10000000,
      });
      const dataArray = Array.isArray(response.data?.result)
        ? response.data.result
        : [];
      setMergeProformas(dataArray);
    } catch (error) {
      console.error("Failed to load proformas for merge:", error);
      setMergeProformas([]);
      setMergeMessage("Failed to load proformas. Please try again.");
    } finally {
      setMergeFetching(false);
    }
  };

  const toggleMergeSelect = (proformaId) => {
    setMergeSelectedIds((prev) =>
      prev.includes(proformaId)
        ? prev.filter((id) => id !== proformaId)
        : [...prev, proformaId]
    );
  };

  const toggleMergeSelectAll = (selectableIds, allSelected) => {
    if (allSelected) {
      setMergeSelectedIds((prev) =>
        prev.filter((id) => !selectableIds.includes(id))
      );
    } else {
      setMergeSelectedIds((prev) =>
        Array.from(new Set([...prev, ...selectableIds]))
      );
    }
  };

  const getMergeTargetProformas = () => {
    if (mergeMode === "manual") {
      return mergeProformas.filter((p) => mergeSelectedIds.includes(p.id));
    }

    let start;
    let end;
    if (mergeMode === "month") {
      start = dayjs(mergeFromMonth).startOf("month");
      end = dayjs(mergeToMonth).endOf("month");
    } else {
      start = dayjs(mergeFromDate).startOf("day");
      end = dayjs(mergeToDate).endOf("day");
    }

    if (start.isAfter(end)) {
      const tmp = start;
      start = end;
      end = tmp;
    }

    return mergeProformas.filter((p) => {
      if (!p.order_date) return false;
      const date = dayjs(p.order_date);
      return !date.isBefore(start) && !date.isAfter(end);
    });
  };

  const buildMergeFileName = () => {
    if (mergeMode === "manual") {
      return `Proforma_Selected_${dayjs().format("DD-MM-YYYY_HH-mm")}.pdf`;
    }
    if (mergeMode === "month") {
      const fromLabel = dayjs(mergeFromMonth).format("MM-YYYY");
      const toLabel = dayjs(mergeToMonth).format("MM-YYYY");
      return fromLabel === toLabel
        ? `Proforma_${fromLabel}.pdf`
        : `Proforma_${fromLabel}_to_${toLabel}.pdf`;
    }
    const fromLabel = dayjs(mergeFromDate).format("DD-MM-YYYY");
    const toLabel = dayjs(mergeToDate).format("DD-MM-YYYY");
    return fromLabel === toLabel
      ? `Proforma_${fromLabel}.pdf`
      : `Proforma_${fromLabel}_to_${toLabel}.pdf`;
  };

  const handleMergeDownload = async () => {
    const targets = getMergeTargetProformas();
    if (targets.length === 0) {
      setMergeMessage("No Proforma Invoices match your selection.");
      return;
    }

    const withReceipts = targets.filter(hasReceipt);
    if (withReceipts.length === 0) {
      setMergeMessage(
        `${targets.length} Proforma Invoice(s) matched, but none have a receipt PDF to merge.`
      );
      return;
    }

    try {
      setMerging(true);
      setMergeMessage("");

      const mergedPdf = await PDFDocument.create();
      let added = 0;
      let failed = 0;

      const ordered = [...withReceipts].sort(
        (a, b) => dayjs(a.order_date).valueOf() - dayjs(b.order_date).valueOf()
      );

      for (const proforma of ordered) {
        try {
          const res = await fetch(proforma.pdf_url);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const buf = await res.arrayBuffer();
          const src = await PDFDocument.load(buf, { ignoreEncryption: true });
          const pages = await mergedPdf.copyPages(src, src.getPageIndices());
          pages.forEach((page) => mergedPdf.addPage(page));
          added += 1;
        } catch (err) {
          console.error(
            `Failed to add receipt for proforma ${proforma.proforma_number}:`,
            err
          );
          failed += 1;
        }
      }

      if (added === 0) {
        setMergeMessage(
          "Could not read any receipt PDFs. They may be unavailable right now."
        );
        return;
      }

      const bytes = await mergedPdf.save();
      saveAs(
        new Blob([bytes], { type: "application/pdf" }),
        buildMergeFileName()
      );

      const skipped = targets.length - withReceipts.length;
      setMergeDialogOpen(false);
      showAlert(
        true,
        `Merged ${added} Proforma Invoice(s) into one PDF` +
        (failed ? `, ${failed} failed` : "") +
        (skipped ? `, ${skipped} without receipt skipped` : "") +
        "."
      );
    } catch (error) {
      console.error("Failed to merge proforma PDFs:", error);
      setMergeMessage("Failed to merge PDFs. Please try again.");
    } finally {
      setMerging(false);
    }
  };

  const mergeFilteredProformas = mergeProformas.filter((p) => {
    const q = mergeSearch.trim().toLowerCase();
    if (!q) return true;
    return (
      String(p.proforma_number || "").toLowerCase().includes(q) ||
      String(p.customer_name || "").toLowerCase().includes(q)
    );
  });
  const mergeTargetProformas = getMergeTargetProformas();
  const mergeMatchedCount = mergeTargetProformas.filter(hasReceipt).length;
  const mergeSelectableIds = mergeFilteredProformas
    .filter(hasReceipt)
    .map((p) => p.id);
  const mergeAllSelected =
    mergeSelectableIds.length > 0 &&
    mergeSelectableIds.every((id) => mergeSelectedIds.includes(id));

  useEffect(() => {
    fetchProformas();
  }, [pageIndex, pageSize, dashFilter]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "proforma_number",
        header: "Proforma No",
        size: 120,
        // Clicking the Proforma number opens the generated Proforma receipt PDF (pdf_url).
        Cell: ({ cell, row }) => {
          const num = cell.getValue();
          const p = row.original;
          if (!num) return "-";
          return hasReceipt(p) ? (
            <a
              href={p.pdf_url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#1976d2", fontWeight: 600, textDecoration: "none" }}
            >
              {num}
            </a>
          ) : (
            num
          );
        },
      },
      {
        accessorKey: "order_date",
        header: "Date",
        size: 100,
        // NOT `filterVariant: "date"` — MRT's built-in date filter needs
        // @mui/x-date-pickers v6 (this app is on v5, `renderInput` API),
        // and crashes ("renderInput is not a function"). Rendering our
        // own v5-compatible MobileDatePicker via the `Filter` override
        // gets a real datepicker without touching MRT's broken one.
        Cell: ({ cell }) =>
          cell.getValue()
            ? dayjs(cell.getValue()).format("DD-MM-YYYY")
            : "-",
        filterFn: (row, columnId, filterValue) => {
          if (!filterValue) return true;
          const rowDate = row.getValue(columnId);
          return (
            !!rowDate && dayjs(rowDate).format("YYYY-MM-DD") === filterValue
          );
        },
        Filter: ({ column }) => (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <MobileDatePicker
              value={column.getFilterValue() ? dayjs(column.getFilterValue()) : null}
              onChange={(date) =>
                column.setFilterValue(date ? date.format("YYYY-MM-DD") : undefined)
              }
              inputFormat="DD-MM-YYYY"
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="standard"
                  size="small"
                  fullWidth
                  placeholder="Filter date"
                />
              )}
            />
          </LocalizationProvider>
        ),
      },
      {
        accessorKey: "customer_name",
        header: "Bill To Company",
        size: 210,
        // Company first, then phone and email underneath — one column instead of three.
        Cell: ({ row }) => {
          const r = row.original;
          return (
            <Box sx={{ lineHeight: 1.3, minWidth: 0 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.3 }} noWrap title={r.customer_name}>
                {r.customer_name || "-"}
              </Typography>
              {(r.customer_phone || r.customer_email) && (
                <Typography sx={{ fontSize: 11.5, color: "text.secondary", lineHeight: 1.3 }} noWrap title={[r.customer_phone, r.customer_email].filter(Boolean).join(" · ")}>
                  {[r.customer_phone, r.customer_email].filter(Boolean).join(" · ")}
                </Typography>
              )}
            </Box>
          );
        },
      },
      { accessorKey: "customer_phone", header: "Phone", size: 120 },
      { accessorKey: "customer_email", header: "Email", size: 180 },
      { accessorKey: "store_name", header: "Store", size: 130, enableSorting: false, Cell: ({ cell }) => cell.getValue() || "-" },
      {
        id: "ordered_products",
        header: "Ordered Products",
        size: 200,
        enableSorting: false,
        enableColumnFilter: false,
        accessorFn: (row) =>
          parseProformaItems(row.items)
            .map((l) => `${l.product_name || "Product"} (${l.sku || "-"}) x ${formatNumber(l.quantity)}`)
            .join(", "),
        Cell: ({ row }) => {
          const lines = parseProformaItems(row.original.items);
          if (lines.length === 0) return "-";
          const label = (l) => `${l.product_name || "Product"} (${l.sku || "-"}) x ${formatNumber(l.quantity)}`;
          return (
            <Tooltip title={<span style={{ whiteSpace: "pre-line" }}>{lines.map(label).join("\n")}</span>} arrow>
              <Box sx={{ lineHeight: 1.3, minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, lineHeight: 1.3 }} noWrap>
                  {lines[0].product_name || "Product"} x {formatNumber(lines[0].quantity)}
                </Typography>
                {lines.length > 1 && (
                  <Typography sx={{ fontSize: 11.5, color: "primary.main", fontWeight: 600, lineHeight: 1.3 }}>
                    +{lines.length - 1} more
                  </Typography>
                )}
              </Box>
            </Tooltip>
          );
        },
      },
      {
        id: "total_qty",
        header: "Qty",
        size: 80,
        enableSorting: false,
        enableColumnFilter: false,
        accessorFn: (row) =>
          parseProformaItems(row.items).reduce((t, l) => t + (parseFloat(l.quantity) || 0), 0),
        Cell: ({ cell }) => (cell.getValue() ? formatNumber(cell.getValue()) : "-"),
      },
      {
        id: "doc_subtotal",
        header: "Subtotal",
        size: 105,
        enableSorting: false,
        enableColumnFilter: false,
        ...numericColumn,
        accessorFn: (row) => docAmounts(row).subtotal,
        Cell: ({ row }) => <AmountCell value={docAmounts(row.original).subtotal} />,
      },
      {
        id: "doc_discount",
        header: "Discount",
        size: 95,
        enableSorting: false,
        enableColumnFilter: false,
        ...numericColumn,
        accessorFn: (row) => docAmounts(row).discount,
        Cell: ({ row }) => <AmountCell value={docAmounts(row.original).discount} zeroAsDash />,
      },
      {
        id: "doc_vat",
        header: "VAT",
        size: 90,
        enableSorting: false,
        enableColumnFilter: false,
        ...numericColumn,
        accessorFn: (row) => docAmounts(row).vat,
        Cell: ({ row }) => <AmountCell value={docAmounts(row.original).vat} zeroAsDash />,
      },
      {
        id: "doc_shipping",
        header: "Shipping",
        size: 95,
        enableSorting: false,
        enableColumnFilter: false,
        ...numericColumn,
        accessorFn: (row) => docAmounts(row).shipping,
        Cell: ({ row }) => <AmountCell value={docAmounts(row.original).shipping} zeroAsDash />,
      },
      {
        accessorKey: "grand_total",
        header: "Grand Total",
        size: 110,
        ...numericColumn,
        Cell: ({ cell }) => <AmountCell value={cell.getValue()} />,
      },
      {
        // Still to collect: Grand Total minus advances held (not yet invoiced).
        accessorKey: "balance_due",
        header: "Balance Due",
        size: 110,
        enableColumnFilter: false,
        ...numericColumn,
        Cell: ({ row }) => {
          const held = Number(row.original.advance_unapplied || 0);
          const due = Number(row.original.balance_due || 0);
          return (
            <AmountCell
              value={due}
              tone={due > 0 ? "amber" : "muted"}
              sub={held > 0 ? `Advance: ${formatAED(held)}` : undefined}
              subTone="green"
            />
          );
        },
      },
      {
        // Everything received so far against this document (all active advances),
        // with the payment mode(s) the money came in by.
        accessorKey: "advance_received",
        header: "Received",
        size: 120,
        enableColumnFilter: false,
        ...numericColumn,
        Cell: ({ row }) => (
          <PaymentsCell
            row={row.original}
            onOpen={(r) =>
              setAdvanceDialog({
                open: true,
                url: `${proformaQuoteBaseAPI}/${r.id}/advances`,
                number: r.proforma_number,
              })
            }
          />
        ),
      },
      {
        // Same wording as the invoice list: Paid / Partially Paid / Unpaid /
        // Overdue / Cancelled — worked out from the advances received.
        id: "payment_status",
        accessorFn: (row) => getDocPaymentStatus(row).label,
        header: "Payment Status",
        size: 130,
        filterVariant: "select",
        filterSelectOptions: DOC_PAYMENT_STATUS_FILTER_OPTIONS,
        enableSorting: false,
        Cell: ({ row }) => {
          const st = getDocPaymentStatus(row.original);
          return (
            <StatusPill
              label={st.label}
              tone={paymentStatusTone(st.label)}
              detail={
                st.overdueDays > 0
                  ? `${st.overdueDays} ${st.overdueDays === 1 ? "day" : "days"} overdue`
                  : st.detail || undefined
              }
              title="View / manage payments"
              onClick={(e) => {
                e.stopPropagation();
                setAdvanceDialog({
                  open: true,
                  url: `${proformaQuoteBaseAPI}/${row.original.id}/advances`,
                  number: row.original.proforma_number,
                });
              }}
            />
          );
        },
      },
      {
        accessorKey: "due_date",
        header: "Due Date",
        size: 100,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          const due = row.original.due_date;
          const days = Number(row.original.credit_days || 0);
          if (!due) return "-";
          return (
            <Box>
              {dayjs(due).format("DD-MM-YYYY")}
              {days > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  {days} credit days
                </Typography>
              )}
            </Box>
          );
        },
      },
      {
        accessorKey: "payment_type",
        header: "Payment Type",
        size: 130,
        enableColumnFilter: false,
        Cell: ({ cell }) => PAYMENT_TYPE_LABELS[Number(cell.getValue())] || "-",
      },
      {
        accessorKey: "payment_method",
        header: "Payment Method",
        size: 150,
        enableColumnFilter: false,
        Cell: ({ cell }) =>
          String(cell.row.original?.payment_type) === "1"
            ? "-"
            : formatPaymentMethod(cell.getValue()),
      },
      {
        accessorKey: "last_payment_date",
        header: "Payment Date",
        size: 120,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ cell }) => (cell.getValue() ? dayjs(cell.getValue()).format("DD-MM-YYYY") : "-"),
      },
      {
        accessorKey: "payment_reference_no",
        header: "Transaction Id",
        size: 130,
        Cell: ({ cell }) => cell.getValue() || "-",
      },
      {
        accessorKey: "payment_attachment_url",
        header: "Payment Attachment",
        enableSorting: false,
        enableColumnFilter: false,
        size: 160,
        Cell: ({ cell }) => {
          const url = cell.getValue();
          if (!url) return "-";
          return (
            <Tooltip title="Open attachment" arrow>
              <Box
                component="a"
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "primary.main", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
              >
                <AttachFileIcon fontSize="small" sx={{ flexShrink: 0 }} />
                View File
              </Box>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 120,
        filterVariant: "select",
        filterSelectOptions: [
          { text: "Active", value: "active" },
          { text: "Converted", value: "converted" },
          { text: "Cancelled", value: "cancelled" },
          { text: "Partial Returned", value: "partial_returned" },
          { text: "Fully Returned", value: "fully_returned" },
        ],
        Cell: ({ cell, row }) => {
          const s = cell.getValue() || "active";
          const statusMap = {
            active: { label: "Active", color: "warning" },
            converted: { label: "Converted", color: "success" },
            cancelled: { label: "Cancelled", color: "error" },
            partial_returned: { label: "Partial Returned", color: "warning" },
            fully_returned: { label: "Fully Returned", color: "info" },
          };
          const { label, color } = statusMap[s] || statusMap.active;
          const clickable =
            s === "converted" ||
            s === "cancelled" ||
            s === "partial_returned" ||
            s === "fully_returned";
          return (
            <Tooltip title={clickable ? "View details" : ""} arrow>
              <Chip
                size="small"
                label={label}
                onClick={clickable ? () => openMeta(row.original) : undefined}
                sx={(theme) => ({
                  bgcolor: alpha(theme.palette[color].main, 0.12),
                  color: theme.palette[color].dark,
                  fontWeight: 700,
                  fontSize: 11,
                  letterSpacing: 0.4,
                  textTransform: "uppercase",
                  border: "none",
                  cursor: clickable ? "pointer" : "default",
                })}
              />
            </Tooltip>
          );
        },
      },
      {
        accessorKey: "created_by_name",
        header: "Created By",
        size: 120,
        Cell: ({ cell }) => cell.getValue() || "-",
      },
      {
        accessorKey: "created_at",
        header: "Created At",
        size: 140,
        Cell: ({ cell }) =>
          cell.getValue() ? dayjs(cell.getValue()).format("DD-MM-YYYY") : "-",
      },
      {
        accessorKey: "updated_at",
        header: "Last Updated",
        size: 140,
        enableColumnFilter: false,
        Cell: ({ cell }) => (cell.getValue() ? dayjs(cell.getValue()).format("DD-MM-YYYY") : "-"),
      },
      // {
      //   id: "receipt",
      //   header: "View Proforma",
      //   size: 150,
      //   enableSorting: false,
      //   enableColumnFilter: false,
      //   Cell: ({ row }) => {
      //     const p = row.original;
      //     return (
      //       <Tooltip title={p.pdf_url ? "" : "No receipt yet"}>
      //         <span>
      //           <Button
      //             size="small"
      //             variant="outlined"
      //             color="primary"
      //             disabled={!p.pdf_url}
      //             startIcon={<RemoveRedEyeIcon fontSize="small" />}
      //             component={p.pdf_url ? "a" : "button"}
      //             href={p.pdf_url || undefined}
      //             target={p.pdf_url ? "_blank" : undefined}
      //             rel={p.pdf_url ? "noopener noreferrer" : undefined}
      //             sx={{ whiteSpace: "nowrap" }}
      //           >
      //             View 
      //           </Button>
      //         </span>
      //       </Tooltip>
      //     );
      //   },
      // },
      {
        id: "actions",
        header: "Actions",
        size: 220,
        minSize: 220,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          const p = row.original;
          return (
            <span
              data-lmggt-handled="true"
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              {["active", "partial_returned"].includes(p.status || "active") ? (
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  disableElevation
                  startIcon={<PublishedWithChangesIcon fontSize="small" />}
                  onClick={() => openConvertDialog(p)}
                  sx={{
                    minWidth: 160,
                    justifyContent: "flex-start",
                    whiteSpace: "nowrap",
                    textTransform: "none",
                  }}
                >
                  Convert to Invoice
                </Button>
              ) : (
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  startIcon={<HistoryIcon fontSize="small" />}
                  onClick={() =>
                    navigate(`/proforma-quote/history/${p.id}`)
                  }
                  sx={{
                    minWidth: 160,
                    justifyContent: "flex-start",
                    whiteSpace: "nowrap",
                    textTransform: "none",
                  }}
                >
                  View History
                </Button>
              )}
              {["active", "partial_returned", "fully_returned"].includes(
                p.status || "active"
              ) && (
                <Tooltip title="More actions">
                  <IconButton
                    size="small"
                    onClick={(e) =>
                      setActionMenu({ anchorEl: e.currentTarget, row: p })
                    }
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </span>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <Layout>
      <AlertMessage ref={alertRef} />
      <Grid className="employee-form bold-header">
        <Grid container spacing={3}>
          <Grid
            textAlign="center"
            item
            className="form-lable"
            xs={12}
            lg={6}
            md={6}
          >
            <Typography className="page_title" textAlign="left">
              {location.state?.reportTitle || "Proforma Invoice List"}
            </Typography>
            <Breadcrumbs aria-label="breadcrumb">
              <Link underline="hover" color="inherit" href="/dashboard">
                Dashboard
              </Link>
              <Typography color="text.primary">
                Proforma Invoice
              </Typography>
            </Breadcrumbs>
          </Grid>
          <Grid
            textAlign="right"
            item
            className="form-lable"
            xs={12}
            lg={6}
            md={6}
          >
            <LoadingButton
              size="medium"
              variant="contained"
              color="success"
              className="button_first"
              onClick={() => navigate("/proforma-quote/add")}
              startIcon={<AddIcon />}
            >
              Proforma Invoice
            </LoadingButton>
            <LoadingButton
              size="medium"
              variant="contained"
              color="primary"
              onClick={() => navigate(-1)}
              startIcon={<ArrowBackIosIcon />}
            >
              Back
            </LoadingButton>
          </Grid>
        </Grid>

        <ListFilterBar
          showProduct
          value={bar}
          onChange={applyBar}
          onClear={() => {
            setPageIndex(0);
            setDashFilter(null);
            setBar(emptyListFilters);
          }}
        />

        {dashFilter && (
          <Alert
            severity="info"
            sx={{ mb: 1.5 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  setPageIndex(0);
                  setDashFilter(null);
                  setBar(emptyListFilters);
                }}
              >
                Clear filter
              </Button>
            }
          >
            Filtered from the dashboard
            {dashFilter.payment_method ? ` — ${dashFilter.payment_method}` : ""}
            {dashFilter.status_scope === "not_cancelled" ? " — excluding cancelled" : ""}
            {dashFilter.from_date || dashFilter.to_date
              ? ` — ${dashFilter.from_date ? dayjs(dashFilter.from_date).format("DD-MM-YYYY") : "start"} to ${dashFilter.to_date ? dayjs(dashFilter.to_date).format("DD-MM-YYYY") : "today"}`
              : ""}
            .
          </Alert>
        )}

        <Grid className="form_design">
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <MaterialReactTable
              columns={columns}
              data={proformaList}
              state={{
                isLoading: loading,
                pagination: { pageIndex, pageSize },
                columnVisibility,
              }}
              manualPagination
              rowCount={totalCount}
              onPaginationChange={(updater) => {
                const paginationState = updater({ pageIndex, pageSize });
                setPageIndex(paginationState.pageIndex);
                setPageSize(paginationState.pageSize);
              }}
              onColumnVisibilityChange={changeColumnVisibility}
              enableGlobalFilter={false}
              enableFullScreenToggle={false}
              enableStickyHeader
              enableRowNumbers
              enableColumnPinning
              initialState={{
                columnPinning: {
                  left: ["mrt-row-numbers", "proforma_number"],
                  right: ["actions"],
                },
              }}
              className="mat-react-tbl"
              layoutMode="grid"
              muiTableContainerProps={{
                sx: {
                  maxHeight: "calc(100vh - 240px)",
                  minHeight: 450,
                  overflowY: "auto",
                },
              }}
              muiTableBodyCellProps={{
                sx: {
                  padding: "3px 8px",
                  border: "1px solid #ccc",
                },
              }}
              muiTableHeadCellProps={{
                sx: {
                  padding: "8px",
                  border: "1px solid #ccc",
                  color: "#fff !important",
                  fontWeight: "bold",
                  backgroundColor: "#0c54a0",
                  "& svg": {
                    color: "#fff !important",
                  },
                  "& .MuiTableSortLabel-root": {
                    color: "#fff !important",
                    "&.Mui-active": {
                      color: "#fff !important",
                      "& .MuiTableSortLabel-icon": {
                        color: "#fff !important",
                      },
                    },
                  },
                  "& .MuiTableSortLabel-icon": {
                    color: "#fff !important",
                  },
                  "& .MuiInputBase-input": {
                    color: "#fff !important",
                    textFillColor: "#fff !important",
                  },
                  "& .MuiInputBase-input::placeholder": {
                    color: "#fff !important",
                    opacity: 0.8,
                  },
                  "& .MuiInput-underline:before": {
                    borderBottomColor: "#fff !important",
                  },
                  "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                    borderBottomColor: "#fff !important",
                  },
                  "& .MuiInput-underline:after": {
                    borderBottomColor: "#fff !important",
                  },
                },
              }}
              muiTableBodyRowProps={({ row }) => ({
                sx: {
                  borderBottom: "1px solid #ccc",
                  backgroundColor: row.index % 2 === 0 ? "#ffffff" : "#e3f2fd",
                  "& td[data-pinned='true']": {
                    backgroundColor:
                      row.index % 2 === 0 ? "#ffffff" : "#e3f2fd",
                  },
                },
              })}
              renderBottomToolbarCustomActions={() => (
                <ListTotalsFooter
                  items={[
                    { label: "Total Amount", sub: `${(listTotals?.active_count ?? totalCount)} ${(listTotals?.active_count ?? totalCount) === 1 ? "record" : "records"}${totalCount > (listTotals?.active_count ?? totalCount) ? ` · ${totalCount - listTotals.active_count} cancelled excluded` : ""}`, value: `${formatAED(listTotals?.grand_total || 0)}` },
                    { label: "Advance Received", value: `${formatAED(listTotals?.advance_unapplied || 0)}`, tone: "green" },
                    { label: "Balance Due", value: `${formatAED(listTotals?.balance_due || 0)}`, tone: "amber" },
                    ...(cancelledRecv.total > 0
                      ? [{ label: `Cancelled (${cancelledRecv.count}) received`, value: `${formatAED(cancelledRecv.total)}`, tone: "red", onClick: () => setCancelledOpen(true) }]
                      : []),
                  ]}
                />
              )}
              renderTopToolbarCustomActions={() => (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    p: "0.5rem",
                    flexWrap: "wrap",
                  }}
                >
                  <Tooltip title="Export data to excel sheet" arrow>
                    <Button
                      onClick={handleExportData}
                      style={{ background: "#1a73e829" }}
                      startIcon={<FileDownloadIcon />}
                    >
                      Export To Excel
                    </Button>
                  </Tooltip>
                  <Tooltip
                    title="Download multiple Proforma Invoices as one merged PDF"
                    arrow
                  >
                    <Button
                      onClick={openMergeDialog}
                      style={{ background: "#1a73e829" }}
                      startIcon={<PictureAsPdfIcon />}
                    >
                      Download CDNs (PDF)
                    </Button>
                  </Tooltip>
                </Box>
              )}
              renderEmptyRowsFallback={() => (
                <Typography sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>
                  No Proforma Invoices generated yet.
                </Typography>
              )}
            />
          </LocalizationProvider>
        </Grid>
      </Grid>

      <Menu
        anchorEl={actionMenu?.anchorEl}
        open={Boolean(actionMenu)}
        onClose={() => setActionMenu(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            navigate(`/proforma-quote/history/${actionMenu.row.id}`);
            setActionMenu(null);
          }}
        >
          <ListItemIcon>
            <HistoryIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View History</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            const r = actionMenu.row;
            setAdvanceDialog({ open: true, url: `${proformaQuoteBaseAPI}/${r.id}/advances`, number: r.proforma_number });
            setActionMenu(null);
          }}
        >
          <ListItemIcon>
            <PaymentsOutlinedIcon fontSize="small" color="success" />
          </ListItemIcon>
          <ListItemText>Payments</ListItemText>
        </MenuItem>

        {["partial_returned", "fully_returned"].includes(
          actionMenu?.row?.status || "active"
        ) && (
          <MenuItem
            onClick={() => {
              openGrnView(actionMenu.row);
              setActionMenu(null);
            }}
          >
            <ListItemIcon>
              <RemoveRedEyeIcon fontSize="small" color="action" />
            </ListItemIcon>
            <ListItemText>View GRN</ListItemText>
          </MenuItem>
        )}
        {["active", "partial_returned"].includes(
          actionMenu?.row?.status || "active"
        ) && [
            <MenuItem
              key="generate-grn"
              onClick={() => {
                navigate(`/proforma-quote/grn/${actionMenu.row.id}`);
                setActionMenu(null);
              }}
            >
              <ListItemIcon>
                <AssignmentReturnedIcon fontSize="small" color="success" />
              </ListItemIcon>
              <ListItemText>Generate GRN</ListItemText>
            </MenuItem>,
            <MenuItem
              key="edit"
              disabled={(actionMenu?.row?.status || "active") !== "active"}
              onClick={() => {
                navigate(`/proforma-quote/edit/${actionMenu.row.id}`);
                setActionMenu(null);
              }}
            >
              <ListItemIcon>
                <EditIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText>Edit</ListItemText>
            </MenuItem>,
            <MenuItem
              key="cancel"
              onClick={() => {
                openCancel(actionMenu.row);
                setActionMenu(null);
              }}
            >
              <ListItemIcon>
                <CancelIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText sx={{ color: "error.main" }}>Cancel</ListItemText>
            </MenuItem>,
          ]}
      </Menu>

      <Dialog
        open={mergeDialogOpen}
        onClose={() => !merging && setMergeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            backgroundColor: "#1976d2",
            color: "#fff",
            py: 2,
          }}
        >
          <PictureAsPdfIcon sx={{ color: "#fff" }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              Download Invoices as One PDF
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "rgba(255,255,255,0.85)" }}
            >
              Merge multiple Proforma Invoice receipts into a single file
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 3, backgroundColor: "#fafbfc" }}>
          <FormControl fullWidth size="small" sx={{ mb: 2.5 }}>
            <InputLabel id="proforma-merge-mode-label">Selection Mode</InputLabel>
            <Select
              labelId="proforma-merge-mode-label"
              label="Selection Mode"
              value={mergeMode}
              onChange={(e) => {
                setMergeMode(e.target.value);
                setMergeMessage("");
              }}
              sx={{ backgroundColor: "#fff" }}
            >
              <MenuItem value="month">By Month Range</MenuItem>
              <MenuItem value="date">By Date Range</MenuItem>
              <MenuItem value="manual">Manual Selection</MenuItem>
            </Select>
          </FormControl>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            {mergeMode === "month" && (
              <Box sx={{ display: "flex", gap: 2 }}>
                <MobileDatePicker
                  label="From Month"
                  views={["year", "month"]}
                  openTo="month"
                  inputFormat="DD-MM-YYYY"
                  value={mergeFromMonth}
                  maxDate={mergeToMonth}
                  onChange={(v) => v && setMergeFromMonth(v)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      size="small"
                      sx={{ backgroundColor: "#fff" }}
                    />
                  )}
                />
                <MobileDatePicker
                  label="To Month"
                  views={["year", "month"]}
                  openTo="month"
                  inputFormat="DD-MM-YYYY"
                  value={mergeToMonth}
                  minDate={mergeFromMonth}
                  onChange={(v) => v && setMergeToMonth(v)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      size="small"
                      sx={{ backgroundColor: "#fff" }}
                    />
                  )}
                />
              </Box>
            )}

            {mergeMode === "date" && (
              <Box sx={{ display: "flex", gap: 2 }}>
                <MobileDatePicker
                  label="From Date"
                  inputFormat="DD-MM-YYYY"
                  value={mergeFromDate}
                  maxDate={mergeToDate}
                  onChange={(v) => v && setMergeFromDate(v)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      size="small"
                      sx={{ backgroundColor: "#fff" }}
                    />
                  )}
                />
                <MobileDatePicker
                  label="To Date"
                  inputFormat="DD-MM-YYYY"
                  value={mergeToDate}
                  minDate={mergeFromDate}
                  onChange={(v) => v && setMergeToDate(v)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      size="small"
                      sx={{ backgroundColor: "#fff" }}
                    />
                  )}
                />
              </Box>
            )}
          </LocalizationProvider>

          {mergeMode === "manual" && (
            <Box>
              <TextField
                fullWidth
                size="small"
                placeholder="Search Proforma no. / customer"
                value={mergeSearch}
                onChange={(e) => setMergeSearch(e.target.value)}
                sx={{ mb: 1, backgroundColor: "#fff" }}
              />
              <Box
                sx={{
                  maxHeight: 300,
                  overflowY: "auto",
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                  backgroundColor: "#fff",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 1.5,
                    py: 0.5,
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    backgroundColor: "#f5f7fa",
                    borderBottom: "1px solid #e0e0e0",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 700, color: "text.secondary" }}
                  >
                    {mergeSelectedIds.length} selected
                  </Typography>
                  <Button
                    size="small"
                    disabled={mergeSelectableIds.length === 0}
                    onClick={() =>
                      toggleMergeSelectAll(mergeSelectableIds, mergeAllSelected)
                    }
                  >
                    {mergeAllSelected ? "Clear all" : "Select all"}
                  </Button>
                </Box>
                {mergeFetching ? (
                  <Box sx={{ p: 2, textAlign: "center" }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : mergeFilteredProformas.length === 0 ? (
                  <Typography sx={{ p: 2 }} variant="body2">
                    No Proforma Invoices found.
                  </Typography>
                ) : (
                  mergeFilteredProformas.map((p) => {
                    const receipt = hasReceipt(p);
                    const selected = mergeSelectedIds.includes(p.id);
                    return (
                      <Box
                        key={p.id}
                        onClick={() => receipt && toggleMergeSelect(p.id)}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          borderBottom: "1px solid #f0f0f0",
                          cursor: receipt ? "pointer" : "default",
                          backgroundColor: selected ? "#e3f2fd" : "transparent",
                          "&:hover": {
                            backgroundColor: receipt
                              ? selected
                                ? "#d7ebfb"
                                : "#f5f7fa"
                              : "transparent",
                          },
                        }}
                      >
                        <Checkbox
                          size="small"
                          checked={selected}
                          onChange={() => toggleMergeSelect(p.id)}
                          onClick={(e) => e.stopPropagation()}
                          disabled={!receipt}
                        />
                        <Box sx={{ flexGrow: 1, py: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {p.proforma_number} - {p.customer_name || "NA"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {p.order_date
                              ? dayjs(p.order_date).format("DD-MM-YYYY")
                              : "NA"}
                            {receipt ? "" : " - No receipt"}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })
                )}
              </Box>
            </Box>
          )}

          <Box
            sx={{
              mt: 2.5,
              px: 1.5,
              py: 1.25,
              borderRadius: 1,
              display: "flex",
              alignItems: "center",
              gap: 1,
              backgroundColor: "#e8f0fe",
              border: "1px solid #c5dbfa",
            }}
          >
            {mergeFetching ? (
              <CircularProgress size={16} />
            ) : (
              <PictureAsPdfIcon fontSize="small" sx={{ color: "#1976d2" }} />
            )}
            <Typography
              variant="body2"
              sx={{ color: "#0d47a1", fontWeight: 600 }}
            >
              {mergeFetching
                ? "Loading Proforma Invoices..."
                : `${mergeMatchedCount} Proforma Invoice(s) with a receipt will be included`}
            </Typography>
          </Box>

          {mergeMessage && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {mergeMessage}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setMergeDialogOpen(false)}
            color="inherit"
            variant="outlined"
            disabled={merging}
          >
            Cancel
          </Button>
          <LoadingButton
            onClick={handleMergeDownload}
            color="primary"
            variant="contained"
            loading={merging}
            disabled={merging || mergeFetching || mergeMatchedCount === 0}
            startIcon={<PictureAsPdfIcon />}
          >
            Download PDF
          </LoadingButton>
        </DialogActions>
      </Dialog>



      {/* Partial Convert Dialog */}
      <Dialog
        open={Boolean(convertDialog)}
        onClose={() => !convertDialog?.loading && setConvertDialog(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 1.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
            Convert to Invoice
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 1,
              mt: 0.75,
            }}
          >
            <Chip
              size="small"
              label={convertDialog?.row?.proforma_number}
              sx={{ fontWeight: 600 }}
            />
            {convertDialog?.row?.customer_name && (
              <Typography variant="body2" color="text.secondary">
                {convertDialog.row.customer_name}
              </Typography>
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: "action.hover", py: 2.5 }}>
          {convertDialog?.paymentType === "1" && (
            <Alert severity="warning" sx={{ mb: 2.5, borderRadius: 1.5 }}>
              Converting with <b>Credit Payment Terms</b> will produce a{" "}
              <b>CREDIT INVOICE</b> — no payment is collected now. Are you sure
              you want to proceed?
            </Alert>
          )}

          {/* Section 1 — Items */}
          <Box
            sx={{
              bgcolor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              p: 2,
              mb: 2.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <Inventory2Icon fontSize="small" color="primary" />
              <Typography sx={{ fontWeight: 700, fontSize: 15 }}>
                Items to Convert
              </Typography>
              {Boolean(convertDialog?.items?.length) && (
                <Chip
                  size="small"
                  label={`${convertDialog.selectedItems.length} of ${convertDialog.items.length} selected`}
                  sx={{ ml: "auto" }}
                />
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Select the specific items you want to convert to an invoice.
              Any items left unselected will remain on this Proforma Invoice as a next version.
            </Typography>
            {convertDialog?.itemsLoading ? (
              <Box sx={{ textAlign: "center", py: 3 }}>
                <CircularProgress size={26} />
              </Box>
            ) : !convertDialog?.items?.length ? (
              <Typography color="text.secondary">
                Nothing left to convert on this Proforma Invoice.
              </Typography>
            ) : (
            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1.5,
                overflow: "hidden",
              }}
            >
              {["partial_returned", "fully_returned"].includes(
                convertDialog?.row?.status
              ) && (
                <Alert severity="info" sx={{ borderRadius: 0 }}>
                  Quantities below already exclude units returned via a GRN —
                  only what's still with the customer is shown.
                </Alert>
              )}
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  textAlign: "left",
                }}
              >
                <thead>
                  <tr>
                    <th style={{ padding: "10px 8px", borderBottom: "1px solid #e0e0e0", backgroundColor: "rgba(25, 118, 210, 0.06)" }}>
                      <Checkbox
                        size="small"
                        checked={
                          convertDialog.selectedItems.length > 0 &&
                          convertDialog.selectedItems.length === convertDialog.items.length
                        }
                        indeterminate={
                          convertDialog.selectedItems.length > 0 &&
                          convertDialog.selectedItems.length < convertDialog.items.length
                        }
                        onChange={(e) => {
                          if (e.target.checked) {
                            setConvertDialog((prev) => ({
                              ...prev,
                              selectedItems: prev.items.map((i) => i.product_id),
                            }));
                          } else {
                            setConvertDialog((prev) => ({
                              ...prev,
                              selectedItems: [],
                            }));
                          }
                        }}
                      />
                    </th>
                    <th style={{ padding: "10px 8px", borderBottom: "1px solid #e0e0e0", backgroundColor: "rgba(25, 118, 210, 0.06)", fontSize: 13, fontWeight: 700 }}>
                      Product
                    </th>
                    <th style={{ padding: "10px 8px", borderBottom: "1px solid #e0e0e0", backgroundColor: "rgba(25, 118, 210, 0.06)", fontSize: 13, fontWeight: 700 }}>
                      Quantity
                    </th>
                    <th style={{ padding: "10px 8px", borderBottom: "1px solid #e0e0e0", backgroundColor: "rgba(25, 118, 210, 0.06)", fontSize: 13, fontWeight: 700 }}>
                      Price
                    </th>
                    <th style={{ padding: "10px 8px", borderBottom: "1px solid #e0e0e0", backgroundColor: "rgba(25, 118, 210, 0.06)", fontSize: 13, fontWeight: 700 }}>
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {convertDialog.items.map((item) => (
                    <tr key={item.product_id}>
                      <td style={{ padding: "8px", borderBottom: "1px solid #eee" }}>
                        <Checkbox
                          size="small"
                          checked={convertDialog.selectedItems.includes(
                            item.product_id
                          )}
                          onChange={(e) => {
                            setConvertDialog((prev) => {
                              const newSelected = e.target.checked
                                ? [...prev.selectedItems, item.product_id]
                                : prev.selectedItems.filter(
                                  (id) => id !== item.product_id
                                );
                              return { ...prev, selectedItems: newSelected };
                            });
                          }}
                        />
                      </td>
                      <td style={{ padding: "8px", borderBottom: "1px solid #eee" }}>
                        <Typography variant="body2">{item.product_name}</Typography>
                        {item.sku ? (
                          <Typography variant="caption" color="text.secondary">
                            SKU: {item.sku}
                          </Typography>
                        ) : null}
                      </td>
                      <td style={{ padding: "8px", borderBottom: "1px solid #eee" }}>
                        <TextField
                          size="small"
                          type="number"
                          value={
                            convertDialog.convertQuantities?.[item.product_id] ??
                            item.quantity
                          }
                          disabled={
                            !convertDialog.selectedItems.includes(item.product_id)
                          }
                          inputProps={{ min: 1, max: item.quantity, style: { width: 70 } }}
                          onFocus={(e) => e.target.select()}
                          onChange={(e) => {
                            // Item 7: never exceed what's actually available
                            // to convert on this line.
                            let val = Number(e.target.value);
                            if (isNaN(val)) val = "";
                            if (val > item.quantity) val = item.quantity;
                            if (val < 0) val = 0;
                            setConvertDialog((prev) => ({
                              ...prev,
                              convertQuantities: {
                                ...prev.convertQuantities,
                                [item.product_id]: val,
                              },
                            }));
                          }}
                        />
                        <Typography variant="caption" color="text.secondary" display="block">
                          of {item.quantity} available
                        </Typography>
                      </td>
                      <td style={{ padding: "8px", borderBottom: "1px solid #eee" }}>
                        {formatAED(item.selling_price)}
                      </td>
                      <td style={{ padding: "8px", borderBottom: "1px solid #eee" }}>
                        {formatAED(item.total_price)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
            )}
          </Box>

          {/* Advance Payment — its own section, like Items to Convert and Mode
              of Payment: Grand Total, Advance Paid and Pending. */}
          {convertSplit && (
            <ConvertPaymentSummary
              invoiceTotal={convertSplit.invoiceTotal}
              advanceApplied={convertSplit.advanceApplied}
              onViewPayments={() =>
                setAdvanceDialog({
                  open: true,
                  url: `${proformaQuoteBaseAPI}/${convertDialog.row.id}/advances`,
                  number: convertDialog.row.proforma_number,
                })
              }
            />
          )}

          {/* Section 2 — Mode of Payment: defaults to whatever was recorded
              on the Proforma; editable here, or leave as-is to carry it
              through unchanged. */}
          <Box
            sx={{
              bgcolor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              p: 2,
              mb: 2.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
              <PaymentsIcon fontSize="small" color="primary" />
              <Typography sx={{ fontWeight: 700, fontSize: 15 }}>
                Mode of Payment
              </Typography>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} md={convertDialog?.paymentType === "2" ? 6 : 12}>
                <FormControl fullWidth size="small">
                  <InputLabel id="convert-payment-type-label">Payment Type</InputLabel>
                  <Select
                    labelId="convert-payment-type-label"
                    label="Payment Type"
                    value={convertDialog?.paymentType || ""}
                    onChange={(e) => {
                      if (
                        e.target.value === "1" &&
                        convertDialog?.customerCreditDays === null
                      ) {
                        // Blocked: Admin hasn't configured Credit Days for this customer.
                        return;
                      }
                      const val = e.target.value;
                      setConvertDialog((prev) => ({
                        ...prev,
                        paymentType: val,
                        paymentMethod: val === "1" ? "" : prev.paymentMethod,
                      }));
                    }}
                  >
                    <MenuItem
                      value="1"
                      disabled={convertDialog?.customerCreditDays === null}
                    >
                      Credit Payment Terms
                    </MenuItem>
                    <MenuItem value="2">Immediate Payment</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Credit is approved per document: only offered when this Proforma
                  itself carries Admin-approved credit. Otherwise a new document
                  has to be created on Credit and approved separately. */}
              {convertDialog?.customerCreditDays === null && (
                <Grid item xs={12}>
                  <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
                    <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                      Credit isn't available here: this Proforma wasn't approved for
                      credit. Credit is approved per document, so create a new
                      document on Credit Payment Terms to request approval.
                    </Typography>
                  </Alert>
                </Grid>
              )}

              {/* Credit Days & Due Date — read-only, same as the Proforma's
                  own create form: Credit Days is admin-configured on the
                  customer, not user-entered, and Due Date is derived from
                  it. */}
              {convertDialog?.paymentType === "1" && (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Credit Days (set by Admin)"
                      type="number"
                      value={
                        convertDialog?.customerCreditDays ??
                        convertDialog?.row?.credit_days ??
                        ""
                      }
                      fullWidth
                      size="small"
                      disabled
                      helperText="Approved by Admin for this document"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Due Date"
                      value={(() => {
                        if (convertDialog?.row?.due_date) {
                          return dayjs(convertDialog.row.due_date).format("DD-MM-YYYY");
                        }
                        const days =
                          convertDialog?.customerCreditDays ??
                          convertDialog?.row?.credit_days;
                        return days != null
                          ? dayjs().add(Number(days), "day").format("DD-MM-YYYY")
                          : "";
                      })()}
                      fullWidth
                      size="small"
                      disabled
                    />
                  </Grid>
                </>
              )}

              {convertDialog?.billToCompanySource && (
                <Grid item xs={12}>
                  <Alert severity="warning">
                    This Proforma was created with <b>Bill to Company</b>, which is no
                    longer used. Choose a different payment method (or Credit) to convert
                    it to an invoice.
                  </Alert>
                </Grid>
              )}

              {convertDialog?.paymentType === "2" && (
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="convert-payment-method-label">Payment Method</InputLabel>
                    <Select
                      labelId="convert-payment-method-label"
                      label="Payment Method"
                      value={convertDialog?.paymentMethod || ""}
                      onChange={(e) =>
                        setConvertDialog((prev) => ({
                          ...prev,
                          paymentMethod: e.target.value,
                        }))
                      }
                    >
                      {paymentModeOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {/* Reference No. & payment proof — same fields the Proforma's
                  own create form collects for Immediate Payment (Item:
                  convert dialog should mirror whatever payment fields exist
                  there). */}
              {convertDialog?.paymentType === "2" && convertDialog?.paymentMethod && (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Reference No."
                      value={convertDialog?.paymentReferenceNo || ""}
                      onChange={(e) =>
                        setConvertDialog((prev) => ({
                          ...prev,
                          paymentReferenceNo: e.target.value,
                        }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <PaymentAttachmentUpload
                      url={convertDialog?.paymentAttachmentUrl}
                      name={convertDialog?.paymentAttachmentName}
                      uploading={convertDialog?.paymentAttachmentUploading}
                      uploadApi={uploadToVercelAPI}
                      onUploadStart={() =>
                        setConvertDialog((prev) => ({
                          ...prev,
                          paymentAttachmentUploading: true,
                        }))
                      }
                      onUploadSuccess={({ url, name }) =>
                        setConvertDialog((prev) => ({
                          ...prev,
                          paymentAttachmentUrl: url,
                          paymentAttachmentName: name,
                          paymentAttachmentUploading: false,
                        }))
                      }
                      onUploadError={(message) => {
                        setConvertDialog((prev) => ({
                          ...prev,
                          paymentAttachmentUploading: false,
                        }));
                        showAlert(false, message);
                      }}
                      onRemove={() =>
                        setConvertDialog((prev) => ({
                          ...prev,
                          paymentAttachmentUrl: "",
                          paymentAttachmentName: "",
                        }))
                      }
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Box>

          {/* Invoice Preview — a document-style mockup of the invoice this
              conversion will produce: same header fields, Bill To block,
              item table and totals the actual printed PDF shows (see
              generateOrderReceiptPdf in orderMasterModel.js). Tax/shipping/
              discount are split proportionally to the selected items' share
              of the subtotal, mirroring convertProformaInvoice()'s own
              split — the server computes the authoritative figures, this is
              a preview only, not a literal render of the PDF. */}
          {Boolean(convertDialog?.items?.length) && (
            <Box
              sx={{
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                p: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                <ReceiptLongIcon fontSize="small" color="primary" />
                <Typography sx={{ fontWeight: 700, fontSize: 15 }}>
                  Invoice Preview
                </Typography>
              </Box>
              {(() => {
                const row = convertDialog.row || {};
                const doc = convertDialog.sourceDoc || row;
                const selected = convertDialog.items.filter((it) =>
                  convertDialog.selectedItems.includes(it.product_id)
                );
                // Scale the Proforma's OWN already-correct totals by the
                // fraction of quantity being converted, rather than
                // recomputing a subtotal from raw selling_price × qty —
                // that ignored any item-level discount entirely (this
                // dialog's item list has no discount data) and compared a
                // gross figure against row.subtotal (which is net,
                // post-discount), so the ratio — and therefore the whole
                // preview — came out wrong, understating the discount's
                // effect on Grand Total. Mirrors convertProformaInvoice()'s
                // split, just by quantity instead of by discount-aware
                // amount (which needs data this dialog doesn't have) —
                // exact for a full conversion (ratio = 1), a close
                // proportional estimate for a partial one.
                // row.discount_amount is ONLY the document-level "Special
                // Discount" — it says nothing about a per-ITEM discount,
                // which this dialog's item list has no data for at all (the
                // grn-candidate endpoint only returns raw selling_price ×
                // qty). So derive the TRUE total discount — item-level and
                // document-level combined — from the gap between the raw,
                // undiscounted sum of every line (what the item table above
                // actually shows per line) and row.subtotal (net, which
                // already has every discount baked in, whichever level it
                // was applied at).
                                // Mirror convertProformaInvoice() exactly: each line keeps its OWN
                // item discount (pro-rated by the qty being converted), the document's
                // subtotal/tax/shipping/special discount are split by the selected share
                // of the NET (post-item-discount) line value, and Inclusive-mode prices
                // are shown excl. VAT like the printed invoice.
                const qtyOf = (it) =>
                  Number(convertDialog.convertQuantities?.[it.product_id] ?? it.quantity) || 0;
                const lineDiscountOf = (it, qty = qtyOf(it)) => {
                  const issued = Number(it.issued_quantity) || 0;
                  return issued > 0 ? ((Number(it.discount_amount) || 0) * qty) / issued : 0;
                };
                const netOf = (it, qty = qtyOf(it)) =>
                  Math.max(qty * (Number(it.selling_price) || 0) - lineDiscountOf(it, qty), 0);
                const allNet = convertDialog.items.reduce(
                  (sum, it) => sum + netOf(it, Number(it.quantity) || 0),
                  0
                );
                const selectedNet = selected.reduce((sum, it) => sum + netOf(it), 0);
                const ratio =
                  allNet > 0 ? selectedNet / allNet : selected.length > 0 ? 1 : 0;
                const taxRate = Number(doc.tax_type ?? row.tax_type) || 0;
                const exVat =
                  (doc.tax_mode ?? row.tax_mode) === "inclusive" ? 1 / (1 + taxRate / 100) : 1;
                // Stored subtotal is net of every discount (grand = subtotal + tax +
                // shipping); show it gross like the invoice PDF, then the discount.
                const netSubtotal = (Number(doc.subtotal ?? row.subtotal) || 0) * ratio;
                const tax = (Number(doc.tax_amount ?? row.tax_amount) || 0) * ratio;
                const shipping = (Number(doc.shipping_amount ?? row.shipping_amount) || 0) * ratio;
                // Document-level "Special/Total" discount only — item discounts are
                // already inside each line's total (and so inside subtotal).
                const discount = (Number(doc.discount_amount ?? row.discount_amount) || 0) * ratio;
                const subtotal = netSubtotal + discount;
                const grandTotal = netSubtotal + tax + shipping;
                const paymentMethodLabel = paymentModeOptions.find(
                  (o) => o.value === convertDialog.paymentMethod
                )?.label;
                const isCredit = convertDialog.paymentType === "1";
                const paymentMethodText = isCredit
                  ? "Credit"
                  : paymentMethodLabel || "—";
                const paymentStatusText = isCredit ? "Pending" : "Paid";
                const documentTitle = isCredit ? "CREDIT INVOICE" : "TAX INVOICE";

                return (
                  <Box
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1.5,
                      overflow: "hidden",
                      bgcolor: "#fff",
                    }}
                  >
                    {/* Document title header — Order No/Date/Store/etc. right,
                        centered title + rule below, same as the PDF. */}
                    <Box sx={{ p: 2, pb: 1.5 }}>
                      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <Box sx={{ textAlign: "right" }}>
                          <Typography variant="caption" display="block">
                            <b>Order No:</b> (assigned on conversion)
                          </Typography>
                          <Typography variant="caption" display="block">
                            <b>Date:</b>{" "}
                            {doc.order_date
                              ? dayjs(doc.order_date).format("DD-MM-YYYY")
                              : "—"}
                          </Typography>
                          <Typography variant="caption" display="block">
                            <b>Store:</b> {convertDialog.storeName || doc.store_name || "—"}
                          </Typography>
                          {doc.emirate && (
                            <Typography variant="caption" display="block">
                              <b>Emirate:</b> {doc.emirate}
                            </Typography>
                          )}
                          {doc.salesman_name && (
                            <Typography variant="caption" display="block">
                              <b>Salesman:</b> {doc.salesman_name}
                            </Typography>
                          )}
                          <Typography variant="caption" display="block">
                            <b>Payment Method:</b> {paymentMethodText}
                          </Typography>
                          <Typography variant="caption" display="block">
                            <b>Payment Status:</b> {paymentStatusText}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography
                        align="center"
                        sx={{ fontWeight: 800, letterSpacing: 0.5, fontSize: 18, color: "primary.main", mt: 1 }}
                      >
                        {documentTitle}
                      </Typography>
                      <Typography variant="caption" align="center" color="text.secondary" display="block">
                        Converted from: {row.proforma_number || "—"}
                      </Typography>
                    </Box>
                    <Divider sx={{ borderBottomWidth: 2, borderColor: "primary.main" }} />

                    {/* Seller + Customer */}
                    <Box sx={{ p: 2 }}>
                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: "primary.main" }}>
                            Seller Details
                          </Typography>
                          <Divider sx={{ my: 0.5 }} />
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {INVOICE_SELLER.companyName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {INVOICE_SELLER.addressLine}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {INVOICE_SELLER.cityEmirate}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            P.O. Box: {INVOICE_SELLER.poBox}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            TRN: {INVOICE_SELLER.trn}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Email: {INVOICE_SELLER.email} | Ph: {INVOICE_SELLER.phone}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: "primary.main" }}>
                            Customer Details
                          </Typography>
                          <Divider sx={{ my: 0.5 }} />
                          <Typography variant="body2" display="block">
                            <b>Bill To Company:</b> {doc.customer_name || row.customer_name || "NA"}
                          </Typography>
                          <Typography variant="body2" display="block">
                            <b>Email:</b> {doc.customer_email || "NA"}
                          </Typography>
                          <Typography variant="body2" display="block">
                            <b>Mobile:</b> {doc.customer_phone || "NA"}
                          </Typography>
                          <Typography variant="body2" display="block">
                            <b>Emirate:</b> {doc.emirate || "NA"}
                          </Typography>
                          <Typography variant="body2" display="block">
                            <b>Address:</b> {doc.address || "NA"}
                          </Typography>
                          <Typography variant="body2" display="block">
                            <b>P.O.Box:</b> {doc.po_box || "NA"}
                          </Typography>
                          <Typography variant="body2" display="block">
                            <b>VAT No:</b> {doc.vat_no || "NA"}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>

                    {/* Product Details */}
                    <Box sx={{ px: 2 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: "primary.main" }}>
                        Product Details
                      </Typography>
                      <Divider sx={{ my: 0.5 }} />
                    </Box>
                    <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
                      <thead>
                        <tr>
                          <th style={{ padding: "8px 16px", borderBottom: "1px solid #e0e0e0", fontSize: 12, fontWeight: 700 }}>
                            Product
                          </th>
                          <th style={{ padding: "8px 16px", borderBottom: "1px solid #e0e0e0", fontSize: 12, fontWeight: 700, textAlign: "right" }}>
                            Qty
                          </th>
                          <th style={{ padding: "8px 16px", borderBottom: "1px solid #e0e0e0", fontSize: 12, fontWeight: 700, textAlign: "right" }}>
                            Unit Price(Excl. Tax)
                          </th>
                          <th style={{ padding: "8px 16px", borderBottom: "1px solid #e0e0e0", fontSize: 12, fontWeight: 700, textAlign: "right" }}>
                            Discount
                          </th>
                          <th style={{ padding: "8px 16px", borderBottom: "1px solid #e0e0e0", fontSize: 12, fontWeight: 700, textAlign: "right" }}>
                            Total
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {selected.map((it) => {
                          const qty = Number(
                            convertDialog.convertQuantities?.[it.product_id] ?? it.quantity
                          );
                          const lineDiscount = lineDiscountOf(it, qty) * exVat;
                          const lineTotal = netOf(it, qty) * exVat;
                          return (
                            <tr key={it.product_id}>
                              <td style={{ padding: "8px 16px", borderBottom: "1px solid #f0f0f0", fontSize: 13 }}>
                                {it.product_name}
                                {it.sku && ` (SKU: ${it.sku})`}
                              </td>
                              <td style={{ padding: "8px 16px", borderBottom: "1px solid #f0f0f0", fontSize: 13, textAlign: "right" }}>
                                {qty}
                              </td>
                              <td style={{ padding: "8px 16px", borderBottom: "1px solid #f0f0f0", fontSize: 13, textAlign: "right" }}>
                                {formatAED((Number(it.selling_price) || 0) * exVat)}
                              </td>
                              <td style={{ padding: "8px 16px", borderBottom: "1px solid #f0f0f0", fontSize: 13, textAlign: "right", color: "#999" }}>
                                {lineDiscount > 0 ? `-${formatAED(lineDiscount)}` : formatAED(0)}
                              </td>
                              <td style={{ padding: "8px 16px", borderBottom: "1px solid #f0f0f0", fontSize: 13, textAlign: "right", fontWeight: 600 }}>
                                {formatAED(lineTotal)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>

                    {/* Totals */}
                    <Box sx={{ p: 2 }}>
                      <Box sx={{ maxWidth: 280, ml: "auto" }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                          <Typography variant="body2" color="text.secondary">
                            Subtotal:
                          </Typography>
                          <Typography variant="body2">{formatAED(subtotal)}</Typography>
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
                          <Typography variant="body2" color="text.secondary">
                            Tax:
                          </Typography>
                          <Typography variant="body2">+{formatAED(tax)}</Typography>
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
                          <Typography variant="body2" color="text.secondary">
                            Shipping:
                          </Typography>
                          <Typography variant="body2">{formatAED(shipping)}</Typography>
                        </Box>
                        {discount > 0 && (
                          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
                            <Typography variant="body2" color="text.secondary">
                              Discount:
                            </Typography>
                            <Typography variant="body2">-{formatAED(discount)}</Typography>
                          </Box>
                        )}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mt: 1.5,
                            pt: 1.5,
                            borderTop: "2px solid",
                            borderColor: "primary.main",
                          }}
                        >
                          <Typography sx={{ fontWeight: 700 }}>Grand Total:</Typography>
                          <Typography sx={{ fontWeight: 700, fontSize: 18, color: "success.main" }}>
                            {formatAED(grandTotal)}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    {/* Seller Bank Information */}
                    <Box sx={{ px: 2, pb: 2 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: "primary.main" }}>
                        Seller Bank Information
                      </Typography>
                      <Divider sx={{ my: 0.5 }} />
                      <Typography variant="body2" display="block">
                        <b>Bank:</b> {INVOICE_SELLER.bankName}
                      </Typography>
                      <Typography variant="body2" display="block">
                        <b>Account Name:</b> {INVOICE_SELLER.accountName}
                      </Typography>
                      <Typography variant="body2" display="block">
                        <b>IBAN:</b> {INVOICE_SELLER.iban}
                      </Typography>
                    </Box>

                    {/* Terms & Conditions */}
                    {doc.terms && (
                      <Box sx={{ px: 2, pb: 2 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: "primary.main" }}>
                          Terms & Conditions
                        </Typography>
                        <Divider sx={{ my: 0.5 }} />
                        {/* terms is stored/printed as raw HTML from a rich-text
                            editor (see the PDF template's own `${order?.terms}`
                            injection) — render it the same way here instead of
                            printing the markup as literal text. */}
                        <Box
                          sx={{
                            typography: "caption",
                            color: "text.secondary",
                            "& p": { m: 0 },
                            "& ul, & ol": { m: 0, pl: 2.5 },
                          }}
                          dangerouslySetInnerHTML={{ __html: doc.terms }}
                        />
                      </Box>
                    )}

                    <Box sx={{ px: 2, pb: 2 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", fontStyle: "italic" }}
                      >
                        Preview only — the invoice number is assigned and the exact
                        tax/shipping/discount split is finalized by the server on
                        conversion.
                      </Typography>
                    </Box>
                  </Box>
                );
              })()}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, bgcolor: "background.paper" }}>
          <Button
            onClick={() => setConvertDialog(null)}
            color="inherit"
            disabled={convertDialog?.loading}
          >
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            color={convertDialog?.paymentType === "1" ? "warning" : "success"}
            disabled={
              convertDialog?.itemsLoading ||
              !convertDialog?.selectedItems?.some(
                (pid) => Number(convertDialog.convertQuantities?.[pid]) > 0
              ) ||
              !convertDialog?.paymentType ||
              (convertDialog?.paymentType === "2" && !convertDialog?.paymentMethod) ||
              convertDialog?.paymentAttachmentUploading
            }
            loading={convertDialog?.loading}
            onClick={async () => {
              setConvertDialog((prev) => ({ ...prev, loading: true }));
              try {
                // Item 7: send each selected line's product_id AND the
                // (possibly partial) quantity chosen for it, capped server-side
                // too against what's actually available.
                const selectedItemsPayload = convertDialog.selectedItems
                  .map((pid) => ({
                    product_id: pid,
                    quantity: Number(convertDialog.convertQuantities?.[pid]) || 0,
                  }))
                  .filter((i) => i.quantity > 0);
                const result = await apiData(
                  "post",
                  `${proformaQuoteBaseAPI}/${convertDialog.row.id}/convert`,
                  {
                    selectedItems: selectedItemsPayload,
                    payment_type: convertDialog.paymentType,
                    paymentMethod:
                      convertDialog.paymentType === "2" ? convertDialog.paymentMethod : null,
                    paymentReferenceNo:
                      convertDialog.paymentType === "2"
                        ? convertDialog.paymentReferenceNo || ""
                        : "",
                    paymentAttachmentUrl:
                      convertDialog.paymentType === "2"
                        ? convertDialog.paymentAttachmentUrl || null
                        : null,
                    paymentAttachmentName:
                      convertDialog.paymentType === "2"
                        ? convertDialog.paymentAttachmentName || null
                        : null,
                  }
                );
                showAlert(
                  result.success,
                  result.msg ||
                  (result.success
                    ? "Converted to invoice."
                    : "Failed to convert.")
                );
                if (result.success) fetchProformas();
              } catch (error) {
                console.error("Action failed:", error);
                showAlert(false, "Failed to convert.");
              } finally {
                setConvertDialog(null);
              }
            }}
          >
            {convertDialog?.paymentType === "1"
              ? "Yes, Convert to Credit Invoice"
              : "Convert Selected"}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(confirm)}
        title={
          confirm
            ? `${confirmConfig[confirm.type].title}${confirm.number ? ` — ${confirm.number}` : ""
            }`
            : ""
        }
        message={confirm ? confirmConfig[confirm.type].message : ""}
        content={
          confirm?.type === "cancel" ? (
            confirm.impactLoading ? (
              <Box sx={{ textAlign: "center", py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <ReservationImpactTable rows={confirm.impact} mode="cancel" />
            )
          ) : null
        }
        maxWidth={confirm?.type === "cancel" ? "sm" : "xs"}
        confirmText={confirm ? confirmConfig[confirm.type].confirmText : "Confirm"}
        confirmColor={confirm ? confirmConfig[confirm.type].confirmColor : "primary"}
        confirmDisabled={confirm?.type === "cancel" && confirm?.impactLoading}
        loading={confirmLoading}
        onConfirm={runConfirmedAction}
        onClose={() => setConfirm(null)}
      />

      <CancelledReceivedDialog open={cancelledOpen} type="proforma" onClose={() => setCancelledOpen(false)} />

      <AdvanceDetailsDialog
        open={advanceDialog.open}
        advancesUrl={advanceDialog.url}
        docNumber={advanceDialog.number}
        onClose={() => setAdvanceDialog((d) => ({ ...d, open: false }))}
        onChanged={async () => {
          fetchProformas();
          // The Convert dialog (if open underneath) shows what is held — refresh it.
          if (convertDialog?.row) {
            const res = await apiData("get", `${proformaQuoteBaseAPI}/${convertDialog.row.id}/advances`);
            if (res?.success) {
              setConvertDialog((prev) =>
                prev
                  ? { ...prev, heldAdvances: (res.data?.advances || []).filter((a) => Number(a.unapplied_amount) > 0) }
                  : prev
              );
            }
          }
        }}
      />

      <Dialog
        open={Boolean(metaDialog)}
        onClose={() => setMetaDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Proforma Invoice Details
          {metaDialog?.data?.proforma_number
            ? ` — ${metaDialog.data.proforma_number}`
            : ""}
        </DialogTitle>
        <DialogContent dividers>
          {metaDialog?.loading ? (
            <Box sx={{ textAlign: "center", py: 3 }}>
              <CircularProgress size={26} />
            </Box>
          ) : !metaDialog?.data ? (
            <Typography color="text.secondary">
              Could not load details.
            </Typography>
          ) : (
            (() => {
              const d = metaDialog.data;
              const fmt = (v) => (v ? dayjs(v).format("DD-MM-YYYY") : "-");
              const rows = [
                ["Status", (d.status || "").toUpperCase()],
                ["Customer", d.customer_name || "-"],
                ["Store", d.store_name || "-"],
                [
                  "Order Date",
                  d.order_date
                    ? dayjs(d.order_date).format("DD-MM-YYYY")
                    : "-",
                ],
                ["Grand Total", formatAED(d.grand_total)],
                [
                  "Created By",
                  `${d.created_by_name || d.created_by || "-"} · ${fmt(
                    d.created_at
                  )}`,
                ],
              ];
              if (d.status === "converted") {
                rows.push([
                  "Converted By",
                  `${d.updated_by_name || d.updated_by || "-"} · ${fmt(
                    d.updated_at
                  )}`,
                ]);
              } else if (d.status === "cancelled") {
                rows.push([
                  "Cancelled By",
                  `${d.updated_by_name || d.updated_by || "-"} · ${fmt(
                    d.updated_at
                  )}`,
                ]);
              }
              const displayRows = rows.map(([k, v]) => [
                k,
                typeof v === "string"
                  ? v.replace(/\s*(?:Â·|·)\s*/g, " ").replace(/^-\s+/, "")
                  : v,
              ]);
              return (
                <>
                  {displayRows.map(([k, v]) => (
                    <Box key={k} sx={{ display: "flex", py: 0.5 }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ width: 150, flexShrink: 0 }}
                      >
                        {k}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {v}
                      </Typography>
                    </Box>
                  ))}
                  {d.status === "converted" && (
                    <Box sx={{ display: "flex", py: 0.5, alignItems: "center" }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ width: 150, flexShrink: 0 }}
                      >
                        Converted Invoice
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {d.converted_order_receipt ? (
                          <a
                            href={d.converted_order_receipt}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "#1976d2", fontWeight: 600, textDecoration: "none" }}
                          >
                            {d.converted_order_number ||
                              `#${d.converted_order_id || "-"}`}
                          </a>
                        ) : (
                          d.converted_order_number ||
                          `#${d.converted_order_id || "-"}`
                        )}
                      </Typography>
                    </Box>
                  )}
                  {["partial_returned", "fully_returned"].includes(d.status) && (
                    <Box sx={{ display: "flex", py: 0.5, alignItems: "center" }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ width: 150, flexShrink: 0 }}
                      >
                        GRN
                      </Typography>
                      <Link
                        component="button"
                        variant="body2"
                        sx={{ fontWeight: 600 }}
                        onClick={() => openGrnView(d)}
                      >
                        View GRN
                      </Link>
                    </Box>
                  )}
                </>
              );
            })()
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setMetaDialog(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(grnDialog)}
        onClose={() => setGrnDialog(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>GRN Details</DialogTitle>
        <DialogContent dividers>
          {grnDialog?.loading ? (
            <Box sx={{ textAlign: "center", py: 3 }}>
              <CircularProgress size={26} />
            </Box>
          ) : !grnDialog?.data?.length ? (
            <Typography color="text.secondary">No GRN found.</Typography>
          ) : (
            grnDialog.data.map((grn, idx) => (
              <Box
                key={grn.id}
                sx={{ mb: idx < grnDialog.data.length - 1 ? 3 : 0 }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 3,
                    mb: 1,
                    alignItems: "center",
                  }}
                >
                  <Typography variant="subtitle2">
                    GRN #
                    {grn.pdf_url ? (
                      <a
                        href={grn.pdf_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#1976d2", textDecoration: "none" }}
                      >
                        {grn.grn_number}
                      </a>
                    ) : (
                      grn.grn_number
                    )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Date:{" "}
                    {grn.grn_date ? dayjs(grn.grn_date).format("DD-MM-YYYY") : "-"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Created By: {grn.created_by_name || grn.created_by || "-"}
                  </Typography>
                  {grn.pdf_url && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<PictureAsPdfIcon fontSize="small" />}
                      href={grn.pdf_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Download PDF
                    </Button>
                  )}
                </Box>
                {grn.remarks && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Remarks: {grn.remarks}
                  </Typography>
                )}
                <Box
                  sx={{ border: "1px solid #ccc", borderRadius: 1, overflow: "hidden" }}
                >
                  <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
                    <thead style={{ backgroundColor: "#f5f5f5" }}>
                      <tr>
                        <th style={{ padding: "8px", borderBottom: "1px solid #ccc" }}>
                          Product
                        </th>
                        <th style={{ padding: "8px", borderBottom: "1px solid #ccc" }}>
                          Quantity
                        </th>
                        <th style={{ padding: "8px", borderBottom: "1px solid #ccc" }}>
                          Condition
                        </th>
                        <th style={{ padding: "8px", borderBottom: "1px solid #ccc" }}>
                          Damaged Reason
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {(grn.items || []).map((item) => (
                        <tr key={item.id}>
                          <td style={{ padding: "8px", borderBottom: "1px solid #eee" }}>
                            {item.product_name || item.product_id}
                            {item.sku ? ` (${item.sku})` : ""}
                          </td>
                          <td style={{ padding: "8px", borderBottom: "1px solid #eee" }}>
                            {item.quantity}
                          </td>
                          <td style={{ padding: "8px", borderBottom: "1px solid #eee" }}>
                            <Chip
                              size="small"
                              label={item.condition === "damaged" ? "Damaged" : "Good"}
                              color={item.condition === "damaged" ? "error" : "success"}
                              variant="outlined"
                            />
                          </td>
                          <td style={{ padding: "8px", borderBottom: "1px solid #eee" }}>
                            {item.damaged_reason || "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              </Box>
            ))
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setGrnDialog(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default ProformaQuoteList;
